import { useCallback, useEffect, useMemo, useState } from "react";
import { normalizeSlotLabel } from "@/lib/manufacturing/slotLabel";
import {
  listManufacturingMaterialUses,
  setManufacturingRecipeLines,
  type MfgMaterial,
  type MfgMaterialUse,
  type MfgRecipeDetailResponse,
  type MfgRecipeKind,
  type MfgRecipeLineRole,
  type MfgRecipeSummary,
} from "@/lib/manufacturingClient";

export type MfgModelsLineKindFilter = "all" | MfgRecipeKind;

export type MfgModelsLineRow = {
  key: string;
  line_kind: "material" | "component" | "use";
  material_id: string;
  component_recipe_id: string;
  material_use_id: string;
  quantity: string;
  role: MfgRecipeLineRole;
  slot_label: string;
  upgrade_price_delta: string;
};

function makeMaterialRow(materialId: string, quantity = "1"): MfgModelsLineRow {
  return {
    key: crypto.randomUUID(),
    line_kind: "material",
    material_id: materialId,
    component_recipe_id: "",
    material_use_id: "",
    quantity,
    role: "structural",
    slot_label: "",
    upgrade_price_delta: "",
  };
}

function makeComponentRow(recipeId: string, quantity = "1"): MfgModelsLineRow {
  return {
    key: crypto.randomUUID(),
    line_kind: "component",
    material_id: "",
    component_recipe_id: recipeId,
    material_use_id: "",
    quantity,
    role: "structural",
    slot_label: "",
    upgrade_price_delta: "",
  };
}

function makeUseRow(useId: string, quantity = "1"): MfgModelsLineRow {
  return {
    key: crypto.randomUUID(),
    line_kind: "use",
    material_id: "",
    component_recipe_id: "",
    material_use_id: useId,
    quantity,
    role: "structural",
    slot_label: "",
    upgrade_price_delta: "",
  };
}

function lineQuantityArg(quantity: unknown): string {
  return typeof quantity === "string" && quantity.trim() !== "" ? quantity : "1";
}

function lineRowsFromDetail(detail: MfgRecipeDetailResponse | null): MfgModelsLineRow[] {
  if (!detail) return [];
  return detail.lines.map((ln) => ({
    key: ln.id,
    line_kind:
      ln.line_kind === "use" ? "use" : ln.line_kind === "component" ? "component" : "material",
    material_id: ln.material_id ?? "",
    component_recipe_id: ln.component_recipe_id ?? "",
    material_use_id: ln.material_use_id ?? "",
    quantity: String(ln.quantity),
    role: ln.role,
    slot_label: normalizeSlotLabel(ln.slot_label ?? ""),
    upgrade_price_delta:
      typeof ln.upgrade_price_delta === "number" && Number.isFinite(ln.upgrade_price_delta)
        ? String(ln.upgrade_price_delta)
        : "",
  }));
}

export type UseManufacturingModelsLinesArgs = {
  selectedId: string | null;
  detail: MfgRecipeDetailResponse | null;
  materials: MfgMaterial[];
  recipes: MfgRecipeSummary[];
  onSaved: () => void;
  onError: (message: string) => void;
};

export function useManufacturingModelsLines({
  selectedId,
  detail,
  materials,
  recipes,
  onSaved,
  onError,
}: UseManufacturingModelsLinesArgs) {
  const activeMaterials = useMemo(() => materials.filter((m) => m.is_active), [materials]);
  const defaultMaterialId = activeMaterials[0]?.id ?? "";
  const matById = useMemo(() => new Map(materials.map((m) => [m.id, m])), [materials]);

  const [uses, setUses] = useState<MfgMaterialUse[]>([]);
  const [kindFilter, setKindFilter] = useState<MfgModelsLineKindFilter>("all");
  const [lineRows, setLineRows] = useState<MfgModelsLineRow[]>([]);
  const [saveBusy, setSaveBusy] = useState(false);
  const [linesSaved, setLinesSaved] = useState(false);

  useEffect(() => {
    setLineRows(lineRowsFromDetail(detail));
  }, [detail]);

  useEffect(() => {
    setLinesSaved(false);
  }, [selectedId]);

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      const r = await listManufacturingMaterialUses({ active_only: true });
      if (cancelled || !r.ok) return;
      setUses(r.data.uses);
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  /** User edits clear the post-save indicator; detail reloads keep it. */
  const editLineRows = useCallback((updater: (prev: MfgModelsLineRow[]) => MfgModelsLineRow[]) => {
    setLinesSaved(false);
    setLineRows(updater);
  }, []);

  const filteredRecipes = useMemo(() => {
    const base = recipes.filter((r) => r.id !== selectedId);
    if (kindFilter === "all") return base;
    return base.filter((r) => r.kind === kindFilter);
  }, [recipes, selectedId, kindFilter]);

  const defaultComponentRecipeId =
    filteredRecipes[0]?.id ?? recipes.filter((r) => r.id !== selectedId)[0]?.id ?? "";
  const defaultUseId = uses[0]?.id ?? "";

  const draftMaterialsEstimate = useMemo(() => {
    return lineRows.reduce((sum, r) => {
      if (r.line_kind !== "material") return sum;
      const q = Number(r.quantity);
      if (!Number.isFinite(q) || q === 0 || !r.material_id) return sum;
      const u = matById.get(r.material_id)?.unit_cost ?? 0;
      return sum + q * u;
    }, 0);
  }, [lineRows, matById]);

  const addMaterialLine = useCallback((quantity?: string) => {
    editLineRows((prev) => [...prev, makeMaterialRow(defaultMaterialId, lineQuantityArg(quantity))]);
  }, [defaultMaterialId, editLineRows]);

  const addComponentLine = useCallback((quantity?: string) => {
    if (!defaultComponentRecipeId) return;
    editLineRows((prev) => [
      ...prev,
      makeComponentRow(defaultComponentRecipeId, lineQuantityArg(quantity)),
    ]);
  }, [defaultComponentRecipeId, editLineRows]);

  const addUseLine = useCallback((quantity?: string) => {
    if (!defaultUseId) return;
    editLineRows((prev) => [...prev, makeUseRow(defaultUseId, lineQuantityArg(quantity))]);
  }, [defaultUseId, editLineRows]);

  const removeLine = useCallback(
    (key: string) => {
      editLineRows((prev) => prev.filter((r) => r.key !== key));
    },
    [editLineRows],
  );

  const setLineKind = useCallback(
    (key: string, line_kind: "material" | "component" | "use") => {
      editLineRows((prev) =>
        prev.map((r) =>
          r.key === key
            ? {
                ...r,
                line_kind,
                material_id: line_kind === "material" ? r.material_id || defaultMaterialId : "",
                component_recipe_id:
                  line_kind === "component" ? r.component_recipe_id || defaultComponentRecipeId : "",
                material_use_id: line_kind === "use" ? r.material_use_id || defaultUseId : "",
              }
            : r,
        ),
      );
    },
    [defaultMaterialId, defaultComponentRecipeId, defaultUseId, editLineRows],
  );

  const setLineMaterial = useCallback(
    (key: string, material_id: string) => {
      editLineRows((prev) => prev.map((r) => (r.key === key ? { ...r, material_id } : r)));
    },
    [editLineRows],
  );

  const setLineComponentRecipe = useCallback(
    (key: string, component_recipe_id: string) => {
      editLineRows((prev) =>
        prev.map((r) => (r.key === key ? { ...r, component_recipe_id } : r)),
      );
    },
    [editLineRows],
  );

  const setLineUse = useCallback(
    (key: string, material_use_id: string) => {
      editLineRows((prev) => prev.map((r) => (r.key === key ? { ...r, material_use_id } : r)));
    },
    [editLineRows],
  );

  const setLineQuantity = useCallback(
    (key: string, quantity: string) => {
      editLineRows((prev) => prev.map((r) => (r.key === key ? { ...r, quantity } : r)));
    },
    [editLineRows],
  );

  const setLineRole = useCallback(
    (key: string, role: MfgRecipeLineRole) => {
      editLineRows((prev) =>
        prev.map((r) =>
          r.key === key
            ? {
                ...r,
                role,
                upgrade_price_delta: role === "upgrade_option" ? r.upgrade_price_delta : "",
              }
            : r,
        ),
      );
    },
    [editLineRows],
  );

  const setLineSlotLabel = useCallback(
    (key: string, slot_label: string) => {
      editLineRows((prev) => prev.map((r) => (r.key === key ? { ...r, slot_label } : r)));
    },
    [editLineRows],
  );

  const setLineUpgradePriceDelta = useCallback(
    (key: string, upgrade_price_delta: string) => {
      editLineRows((prev) =>
        prev.map((r) => (r.key === key ? { ...r, upgrade_price_delta } : r)),
      );
    },
    [editLineRows],
  );

  const onSaveLines = useCallback(async () => {
    if (!selectedId) return;
    const parsed: Array<{
      material_id?: string;
      component_recipe_id?: string;
      material_use_id?: string;
      quantity: number;
      sort_order: number;
      role: MfgRecipeLineRole;
      slot_label: string;
      upgrade_price_delta: number | null;
    }> = [];
    let sort = 0;
    for (const row of lineRows) {
      const q = Number(row.quantity);
      if (!Number.isFinite(q) || q === 0) continue;
      sort += 1;
      const upd = row.upgrade_price_delta.trim();
      const upgrade_price_delta =
        row.role === "upgrade_option" && upd !== "" && Number.isFinite(Number(upd)) ? Number(upd) : null;
      const slot_label = normalizeSlotLabel(row.slot_label);
      const role = row.role;

      if (row.line_kind === "material") {
        const mid = row.material_id.trim();
        if (!mid) continue;
        parsed.push({ material_id: mid, quantity: q, sort_order: sort, role, slot_label, upgrade_price_delta });
      } else if (row.line_kind === "use") {
        const uid = row.material_use_id.trim();
        if (!uid) continue;
        parsed.push({
          material_use_id: uid,
          quantity: q,
          sort_order: sort,
          role,
          slot_label,
          upgrade_price_delta,
        });
      } else {
        const cid = row.component_recipe_id.trim();
        if (!cid) continue;
        if (cid === selectedId) {
          onError("A recipe cannot include itself as a sub-recipe.");
          return;
        }
        parsed.push({
          component_recipe_id: cid,
          quantity: q,
          sort_order: sort,
          role,
          slot_label,
          upgrade_price_delta,
        });
      }
    }
    setSaveBusy(true);
    const res = await setManufacturingRecipeLines({ recipe_id: selectedId, lines: parsed });
    setSaveBusy(false);
    if (!res.ok) {
      onError(res.error);
      return;
    }
    setLinesSaved(true);
    onSaved();
  }, [selectedId, lineRows, onError, onSaved]);

  return {
    activeMaterials,
    uses,
    defaultMaterialId,
    defaultComponentRecipeId,
    defaultUseId,
    kindFilter,
    setKindFilter,
    filteredRecipes,
    lineRows,
    draftMaterialsEstimate,
    saveBusy,
    linesSaved,
    addMaterialLine,
    addComponentLine,
    addUseLine,
    removeLine,
    setLineKind,
    setLineMaterial,
    setLineComponentRecipe,
    setLineUse,
    setLineQuantity,
    setLineRole,
    setLineSlotLabel,
    setLineUpgradePriceDelta,
    onSaveLines,
  };
}
